import { communicationsAccess } from '@app/+authenticated/+communications/communications.access';
import { employeesAccess } from '@app/+authenticated/+employees/employees.access';
import { hiringAccess } from '@app/+authenticated/+hiring/hiring.access';
import { myAccountAccess } from '@app/+authenticated/+my-account/my-account.access';
import { isPayrollVisible, payrollAccess } from '@app/+authenticated/+payroll/payroll.access';
import { authenticatedAccess } from '@app/+authenticated/authenticated.access';
import { insightsAccess } from '@app/+authenticated/insights/insights.access';
import { modalAccess } from '@app/+modals/modal.access';
import { _ } from '@ngx-translate/core';
import { format } from 'date-fns';

import { LabsDialogComponent } from '../labs-dialog/labs-dialog.component';
import { type MenuNode } from './command-palette.model';
import { periodsFor, viewsFor } from './command-palette.period-menu';
import { settingsMenuItems } from './command-palette.settings-menu';

export const MENU: MenuNode[] = [
  { slug: 'dashboard', titleKey: _('Home'), route: ['/dashboard'] },
  {
    slug: 'communications',
    titleKey: _('Communications'),
    children: [
      {
        slug: 'communications.chat',
        titleKey: _('Chat'),
        access: communicationsAccess.chat,
        route: ['/communications/chat'],
      },
      {
        slug: 'communications.news',
        titleKey: _('News'),
        children: [
          { slug: 'communications.news.all', titleKey: _('All messages'), route: ['/communications/news'] },
          {
            slug: 'communications.news.add',
            titleKey: _('Add new message'),
            onSelect: ({ router }) => router.navigate(['', { outlets: { modal: ['news-item'] } }]),
          },
        ],
      },
      {
        slug: 'communications.files',
        titleKey: _('Files'),
        children: [
          {
            slug: 'communications.files.all',
            titleKey: _('All company files'),
            access: communicationsAccess.files,
            route: ['/communications/files'],
          },
          {
            slug: 'communications.files.add',
            titleKey: _('Add new file'),
            access: modalAccess['company-file'],
            onSelect: ({ router }) => router.navigate(['', { outlets: { modal: ['company-file'] } }]),
          },
        ],
      },
    ],
  },
  {
    slug: 'schedule',
    titleKey: _('Schedule'),
    children: [
      ...viewsFor('schedule', 'schedule'),
      {
        slug: 'schedule.add-shift',
        titleKey: _('Add shift'),
        onSelect: ({ router }) => router.navigate(['', { outlets: { modal: ['schedule-shift'] } }]),
      },
      {
        slug: 'schedule.add-required-shift',
        titleKey: _('Add required shift'),
        access: modalAccess['schedule-required-shift'],
        onSelect: ({ router }) => router.navigate(['', { outlets: { modal: ['schedule-required-shift'] } }]),
      },
      {
        slug: 'schedule.add-open-shift',
        titleKey: _('Add open shift'),
        access: modalAccess['schedule-open-shift'],
        onSelect: ({ router }) => router.navigate(['', { outlets: { modal: ['schedule-open-shift'] } }]),
      },
      {
        slug: 'schedule.add-absence',
        titleKey: _('Add absence'),
        access: modalAccess['add-absence'],
        onSelect: ({ router }) => router.navigate(['', { outlets: { modal: ['add-absence'] } }]),
      },
      {
        slug: 'schedule.add-availability',
        titleKey: _('Add availability'),
        access: modalAccess['availability'],
        onSelect: ({ router }) => router.navigate(['', { outlets: { modal: ['availability'] } }]),
      },
      {
        slug: 'schedule.add-event',
        titleKey: _('Add event'),
        onSelect: ({ router }) => router.navigate(['', { outlets: { modal: ['event'] } }]),
      },
      {
        slug: 'schedule.add-forecast',
        titleKey: _('Add forecast'),
        access: modalAccess['manage-forecast/:date'],
        onSelect: ({ router }) => {
          const date = format(new Date(), 'yyyy-MM-dd');
          router.navigate(['', { outlets: { modal: ['manage-forecast', date] } }]);
        },
      },
      {
        slug: 'schedule.add-budget',
        titleKey: _('Add budget'),
        access: modalAccess['manage-budget/:date'],
        onSelect: ({ router }) => {
          const date = format(new Date(), 'yyyy-MM-dd');
          router.navigate(['', { outlets: { modal: ['manage-budget', date] } }]);
        },
      },
    ],
  },
  {
    slug: 'timesheet',
    titleKey: _('Timesheet'),
    access: authenticatedAccess.timesheet,
    children: [
      ...viewsFor('timesheet', 'timesheet'),
      {
        slug: 'timesheet.add-worked-hours',
        titleKey: _('Add worked hours'),
        access: modalAccess['timesheet'],
        onSelect: ({ router }) => router.navigate(['', { outlets: { modal: ['timesheet'] } }]),
      },
      {
        slug: 'timesheet.clock-employee',
        titleKey: _('Clock employee'),
        access: modalAccess['clock-employee'],
        onSelect: ({ router }) => router.navigate(['', { outlets: { modal: ['clock-employee'] } }]),
      },
    ],
  },
  {
    slug: 'payroll',
    titleKey: _('Payroll'),
    // `access` supplies the plan gate the nav deliberately omits: below Basic the palette drops
    // Payroll outright, while the nav keeps showing it locked and upsells (SHB-25320). The
    // palette has no locked-item concept, so the two diverge here on purpose.
    access: payrollAccess.payroll,
    // The Labs master switch is no dimension of an `AccessConstraint`, and turning Labs off has
    // to withdraw every entry point to a Labs feature — so share the nav item's own predicate.
    isVisible: ({ labsEnabled, isFeatureEnabled, permissionState }) =>
      isPayrollVisible(
        { labsEnabled, featureEnabled: isFeatureEnabled(payrollAccess.payroll.featureFlag) },
        permissionState,
      ),
    route: ['/payroll'],
  },
  {
    slug: 'performance',
    titleKey: _('Performance'),
    keywordKeys: [_('Insights')],
    access: insightsAccess.insights,
    children: periodsFor('performance', ['/insights']),
  },
  {
    slug: 'employees',
    titleKey: _('Employees'),
    children: [
      { slug: 'employees.all', titleKey: _('All employees'), access: employeesAccess.employees, route: ['/employees'] },
      {
        slug: 'employees.absence',
        titleKey: _('Absences'),
        access: employeesAccess.absence,
        route: ['/employees/absence'],
      },
      {
        slug: 'employees.templates',
        titleKey: _('Templates'),
        access: employeesAccess.templates,
        route: ['/employees/templates/document'],
      },
      {
        slug: 'employees.search',
        titleKey: _('Search employee'),
        access: employeesAccess.employees,
        dynamicChildren: 'employees',
      },
      {
        slug: 'employees.add',
        titleKey: _('Add employee'),
        access: modalAccess['employee-create'],
        onSelect: ({ router }) => router.navigate(['', { outlets: { modal: ['employee-create'] } }]),
      },
    ],
  },
  {
    slug: 'hiring',
    titleKey: _('Hiring'),
    access: authenticatedAccess.hiring,
    children: [
      {
        slug: 'hiring.new-hires',
        titleKey: _('New hires'),
        access: hiringAccess['new-hires'],
        route: ['/hiring/new-hires'],
      },
      {
        slug: 'hiring.add-new-hire',
        titleKey: _('Add new hire'),
        access: hiringAccess['new-hires'],
        onSelect: ({ router, newHireDialog }) => {
          router.navigate(['/hiring/new-hires']).then(() => {
            newHireDialog.openAddNewHireDialog();
          });
        },
      },
      {
        slug: 'hiring.rehire',
        titleKey: _('Rehire former employee'),
        access: hiringAccess['new-hires'],
        onSelect: ({ router, newHireDialog }) => {
          router.navigate(['/hiring/new-hires']).then(() => {
            newHireDialog.openRehireFormerEmployeeDialog();
          });
        },
      },
      {
        slug: 'hiring.templates',
        titleKey: _('Templates'),
        access: hiringAccess.templates,
        children: [
          { slug: 'hiring.templates.all', titleKey: _('All templates'), route: ['/hiring/templates/document'] },
          {
            slug: 'hiring.templates.add',
            titleKey: _('Add new template'),
            onSelect: ({ router, templateDialog }) => {
              router.navigate(['/hiring/templates/document']).then(() => {
                templateDialog.openAddTemplateDialog();
              });
            },
          },
        ],
      },
    ],
  },
  { slug: 'reports', titleKey: _('Reports'), access: authenticatedAccess.reports, route: ['/reports'] },
  {
    slug: 'checklist',
    titleKey: _('Get Started Checklist'),
    onSelect: ({ sidePanel }) => sidePanel.setActiveComponent('checklist'),
  },
  {
    slug: 'support',
    titleKey: _('Get Support'),
    onSelect: ({ sidePanel }) => sidePanel.setActiveComponent('support'),
  },
  {
    slug: 'settings',
    titleKey: _('Settings'),
    children: settingsMenuItems,
  },
  {
    slug: 'requests',
    titleKey: _('Requests'),
    // Palette-only action (opens the supervisor-requests sidebar); mirrors the
    // permission that reveals that sidebar's supervisor tab.
    access: { permission: ['Approve absentee', 'Approve exchange'] },
    onSelect: ({ header }) => header.supervisorRequestsSidebarOpen$.next(true),
  },
  {
    slug: 'my-requests',
    titleKey: _('My Requests'),
    onSelect: ({ header }) => header.myRequestsSidebarOpen$.next(true),
  },
  {
    slug: 'my-profile',
    titleKey: _('My items'),
    children: [
      { slug: 'my-profile.profile', titleKey: _('Profile'), route: ['/my-account/profile'] },
      { slug: 'my-profile.schedule', titleKey: _('Schedule'), route: ['/my-account/schedule'] },
      { slug: 'my-profile.timesheet', titleKey: _('Timesheet'), route: ['/my-account/timesheet'] },
      { slug: 'my-profile.absences', titleKey: _('Absences'), route: ['/my-account/absence'] },
      { slug: 'my-profile.plus-minus', titleKey: _('Plus minus'), route: ['/my-account/plus-minus'] },
      {
        slug: 'my-profile.minijob',
        titleKey: _('Minijob'),
        access: myAccountAccess.minijob,
        // The page only exists for an employee on a minijob contract, which no constraint
        // field covers — the same reason the nav item checks it.
        isVisible: ({ hasMinijob }) => hasMinijob,
        route: ['/my-account/minijob'],
      },
      { slug: 'my-profile.login', titleKey: _('Login'), route: ['/my-account/login'] },
      { slug: 'my-profile.notifications', titleKey: _('Notifications'), route: ['/my-account/notifications'] },
      { slug: 'my-profile.files', titleKey: _('Files'), route: ['/my-account/files'] },
    ],
  },
  {
    slug: 'labs',
    titleKey: _('Labs'),
    onSelect: ({ dialog }) => {
      dialog.open(LabsDialogComponent, {});
    },
  },
];
